import { useState } from "react";
import { Card } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStore,
  faLocationDot,
  faTag,
  faCalendarAlt,
  faClock,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faMoneyBillWave,
  faArrowUp,
  faArrowDown,
  faChartPie,
  faShoppingCart,
  faReceipt,
  faCoins,
  faWallet,
  faChartLine,
  faInfoCircle,
  faChartBar,
  faFilter,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../../hooks";

const ShopInfoTab = ({ shop, transactions = [] }) => {
  const { hasPermission, hasAllPermissions } = useAuth();
  const [activeTab, setActiveTab] = useState("shopinfo");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [dateError, setDateError] = useState("");

  const canViewFinancialOverview =
    hasPermission(["manage_shop_modules"]) ||
    hasAllPermissions([
      "manage_stock",
      "manage_capital",
      "manage_sales",
      "manage_financial_records",
    ]);

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateShort = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("sw-TZ", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount || 0)) + " TSH";

  const formatCashIn = (amount) => "+ " + formatCurrency(amount);
  const formatCashOut = (amount) => "- " + formatCurrency(amount);

  const getStatusBadge = (status) => {
    const normalized = (status || "ACTIVE").toUpperCase();
    const config = {
      ACTIVE: {
        className: "bg-white text-green-700 border-green-400",
        icon: faCheckCircle,
        iconColor: "text-green-600",
        label: "Active",
      },
      INACTIVE: {
        className: "bg-white text-red-700 border-red-200",
        icon: faTimesCircle,
        iconColor: "text-red-600",
        label: "Inactive",
      },
      SUSPENDED: {
        className: "bg-white text-orange-700 border-orange-300",
        icon: faExclamationTriangle,
        iconColor: "text-orange-600",
        label: "Suspended",
      },
    };
    return config[normalized] || config.ACTIVE;
  };

  const filterTransactionsByDate = (txns) => {
    if (!isFilterApplied || !startDate || !endDate) return txns;

    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    return txns.filter((t) => {
      const d = new Date(t.date);
      return d >= start && d <= end;
    });
  };

  const applyDateFilter = () => {
    if (!startDate || !endDate) {
      setDateError("Please select both start date and end date.");
      return;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (end < start) {
      setDateError("End date must be greater than or equal to start date.");
      return;
    }
    setDateError("");
    setIsFilterApplied(true);
  };

  const handleStartDateChange = (value) => {
    setStartDate(value);
    setDateError("");
    if (endDate && value && new Date(value) > new Date(endDate)) {
      setDateError("Start date cannot be after end date.");
    } else {
      setDateError("");
    }
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    setDateError("");
    if (startDate && value && new Date(value) < new Date(startDate)) {
      setDateError("End date must be greater than or equal to start date.");
    } else {
      setDateError("");
    }
  };

  const clearDateFilter = () => {
    setStartDate("");
    setEndDate("");
    setIsFilterApplied(false);
    setDateError("");
  };

  const InfoItem = ({ icon, label, value, highlight = false }) => (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg border transition-all duration-200 ${
        highlight
          ? "bg-blue-50 border-blue-200"
          : "bg-gray-50 border-gray-100 hover:bg-gray-100"
      }`}
    >
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
          highlight ? "bg-blue-100" : "bg-white border border-gray-200"
        }`}
      >
        <FontAwesomeIcon
          icon={icon}
          className={`h-3.5 w-3.5 ${highlight ? "text-blue-600" : "text-gray-600"}`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm uppercase tracking-wider text-gray-600 font-medium">
          {label}
        </p>
        <p className="text-sm font-medium text-gray-900 truncate">
          {value || "-"}
        </p>
      </div>
    </div>
  );

  const SummaryCard = ({
    icon,
    label,
    value,
    subtitle,
    color,
    bgColor,
    borderColor,
  }) => (
    <Card
      className={`border ${borderColor} ${bgColor} p-4 transition-all duration-200 hover:shadow-sm`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-full ${color} flex items-center justify-center`}
          >
            <FontAwesomeIcon icon={icon} className="h-4 w-4 text-white" />
          </div>
          <div>
            <p className="text-xs text-gray-600 font-medium uppercase tracking-wider">
              {label}
            </p>
            <p className="text-lg font-bold text-gray-900">{value}</p>
            {subtitle && (
              <p className="text-xs text-gray-600 mt-0.5 font-medium">
                {subtitle}
              </p>
            )}
          </div>
        </div>
      </div>
    </Card>
  );

  const status = getStatusBadge(shop?.status);
  const shopType = shop?.type || shop?.shop_type || "N/A";
  const shopName = shop?.name || shop?.shop_name || "N/A";

  const filteredTransactions = filterTransactionsByDate(transactions || []);

  const totalCapital = filteredTransactions
    .filter((t) => t.category === "Capital")
    .reduce((sum, t) => sum + Number(t.amount || 0), 0);
  const capitalCount = filteredTransactions.filter(
    (t) => t.category === "Capital",
  ).length;

  const salesRows = filteredTransactions.filter((t) => t.category === "Sales");
  const purchaseRows = filteredTransactions.filter(
    (t) => t.category === "Purchase",
  );
  const expenseRows = filteredTransactions.filter(
    (t) => t.category === "Expense",
  );
  const receiveRows = filteredTransactions.filter(
    (t) => t.category === "Receive",
  );

  const totalSales = salesRows.reduce(
    (sum, t) => sum + Number(t.amount || 0),
    0,
  );
  const totalPurchases = purchaseRows.reduce(
    (sum, t) => sum + Number(t.amount || 0),
    0,
  );
  const totalExpenses = expenseRows.reduce(
    (sum, t) => sum + Number(t.amount || 0),
    0,
  );
  const totalReceive = receiveRows.reduce(
    (sum, t) => sum + Number(t.amount || 0),
    0,
  );

  const totalIn = filteredTransactions
    .filter((t) => t.type === "IN")
    .reduce((sum, t) => sum + Number(t.amount || 0), 0);
  const totalOut = filteredTransactions
    .filter((t) => t.type === "OUT")
    .reduce((sum, t) => sum + Number(t.amount || 0), 0);

  const netBalance = totalIn - totalOut;
  const profitLoss = totalSales - (totalPurchases + totalExpenses);

  const getDateRangeText = () => {
    if (isFilterApplied && startDate && endDate) {
      return `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`;
    }
    return "All Records";
  };

  const ShopInfoContent = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pb-6">
      <InfoItem
        icon={faStore}
        label="Shop Name"
        value={shopName}
        highlight={true}
      />
      <InfoItem icon={faLocationDot} label="Location" value={shop?.location} />
      <InfoItem icon={faTag} label="Shop Type" value={shopType} />
      <InfoItem
        icon={faCalendarAlt}
        label="Created At"
        value={formatDate(shop?.created_at || shop?.createdAt)}
      />
      <InfoItem
        icon={faClock}
        label="Last Updated"
        value={formatDate(shop?.updated_at || shop?.updatedAt)}
      />
      <div className={`rounded-lg p-4 ${status.className} border`}>
        <div className="flex items-center gap-3">
          <FontAwesomeIcon
            icon={status.icon}
            className={`h-5 w-5 ${status.iconColor}`}
          />
          <div>
            <p className="text-sm font-semibold text-gray-900">
              Status: <span className={status.iconColor}>{status.label}</span>
            </p>
            <p className="text-sm text-gray-600">
              {status.label === "Active"
                ? "Business is fully operational"
                : status.label === "Inactive"
                  ? "Business is currently closed"
                  : "Business operations are suspended"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const FinancialOverviewContent = () => (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon
              icon={faFilter}
              className="text-blue-600 h-4 w-4"
            />
            <span className="text-sm font-medium text-gray-700">
              Filter by date:
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 font-medium">From:</span>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
                className="h-9 text-sm border border-gray-300 rounded-md w-36 sm:w-40"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 font-medium">To:</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => handleEndDateChange(e.target.value)}
                className="h-9 text-sm border border-gray-300 rounded-md w-36 sm:w-40"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={applyDateFilter}
                disabled={!startDate || !endDate || !!dateError}
                className="h-9 bg-blue-800 hover:bg-blue-900 text-white px-4 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply
              </Button>
              {isFilterApplied && (
                <Button
                  onClick={clearDateFilter}
                  variant="outline"
                  className="h-9 border-red-300 text-red-600 hover:bg-red-50 px-4 text-sm"
                >
                  <FontAwesomeIcon icon={faTimes} className="h-3 w-3 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>

        {dateError && (
          <div className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2 flex items-center gap-2">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="h-3.5 w-3.5 text-red-500"
            />
            <span>{dateError}</span>
          </div>
        )}

        {!dateError && (
          <div className="mt-3 flex items-center gap-2 text-xs">
            <span className="text-gray-600">Showing:</span>
            <Badge
              variant="outline"
              className="bg-blue-50 text-blue-700 border-blue-200"
            >
              <FontAwesomeIcon icon={faCalendarAlt} className="h-3 w-3 mr-1" />
              {getDateRangeText()}
            </Badge>
            <span className="text-gray-500">|</span>
            <span className="text-gray-600">
              <strong>{filteredTransactions.length}</strong> transactions found
            </span>
          </div>
        )}
      </div>

      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <FontAwesomeIcon
            icon={faChartPie}
            className="text-blue-600 h-4 w-4"
          />
          Financial Overview
          {isFilterApplied && (
            <Badge
              variant="outline"
              className="text-xs bg-blue-50 text-blue-700 border-blue-200"
            >
              Filtered
            </Badge>
          )}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <SummaryCard
            icon={faCoins}
            label="Total Capital"
            value={formatCurrency(totalCapital)}
            subtitle={`${capitalCount} capital entries`}
            color="bg-blue-600"
            bgColor="bg-blue-50"
            borderColor="border-blue-200"
          />
          <SummaryCard
            icon={faArrowUp}
            label="Total Sales"
            value={formatCurrency(totalSales)}
            subtitle={`${salesRows.length} sales`}
            color="bg-green-600"
            bgColor="bg-green-50"
            borderColor="border-green-200"
          />
          <SummaryCard
            icon={faShoppingCart}
            label="Total Purchases"
            value={formatCurrency(totalPurchases)}
            subtitle={`${purchaseRows.length} purchases`}
            color="bg-orange-600"
            bgColor="bg-orange-50"
            borderColor="border-orange-200"
          />
          <SummaryCard
            icon={faReceipt}
            label="Other Expenses"
            value={formatCurrency(totalExpenses)}
            subtitle={`${expenseRows.length} expenses`}
            color="bg-red-600"
            bgColor="bg-red-50"
            borderColor="border-red-200"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-white border border-gray-200 p-4">
          <h5 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3 flex items-center gap-2">
            <FontAwesomeIcon
              icon={faWallet}
              className="text-blue-600 h-3.5 w-3.5"
            />
            Cash Flow Summary
          </h5>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 rounded-lg bg-green-50 border border-green-200">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faArrowUp}
                  className="text-green-600 h-3.5 w-3.5"
                />
                <span className="text-sm font-medium text-gray-700">
                  Cash IN
                </span>
              </div>
              <span className="text-sm font-bold text-green-700">
                {formatCashIn(totalIn)}
              </span>
            </div>
            <div className="flex items-center justify-between p-2 rounded-lg bg-red-50 border border-red-200">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faArrowDown}
                  className="text-red-600 h-3.5 w-3.5"
                />
                <span className="text-sm font-medium text-gray-700">
                  Cash OUT
                </span>
              </div>
              <span className="text-sm font-bold text-red-700">
                {formatCashOut(totalOut)}
              </span>
            </div>
            <div
              className={`flex items-center justify-between p-2 rounded-lg border-2 ${
                netBalance >= 0
                  ? "bg-gradient-to-r from-green-50 to-emerald-50 border-green-400"
                  : "bg-gradient-to-r from-red-50 to-rose-50 border-red-400"
              }`}
            >
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={netBalance >= 0 ? faArrowUp : faArrowDown}
                  className={
                    netBalance >= 0
                      ? "text-green-600 h-3.5 w-3.5"
                      : "text-red-600 h-3.5 w-3.5"
                  }
                />
                <span className="text-sm font-semibold text-gray-700">
                  Net Balance
                </span>
              </div>
              <span
                className={`text-sm font-bold ${netBalance >= 0 ? "text-green-700" : "text-red-700"}`}
              >
                {formatCurrency(netBalance)}
              </span>
            </div>
          </div>
        </Card>

        <Card className="bg-white border border-gray-200 p-4">
          <h5 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3 flex items-center gap-2">
            <FontAwesomeIcon
              icon={faChartLine}
              className="text-purple-600 h-3.5 w-3.5"
            />
            Profit / Loss Analysis
          </h5>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 rounded-lg bg-green-50 border border-green-200">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faArrowUp}
                  className="text-green-600 h-3.5 w-3.5"
                />
                <span className="text-sm font-medium text-gray-700">
                  Total Revenue (Sales)
                </span>
              </div>
              <span className="text-sm font-bold text-green-700">
                {formatCurrency(totalSales)}
              </span>
            </div>
            <div className="flex items-center justify-between p-2 rounded-lg bg-orange-50 border border-orange-200">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faShoppingCart}
                  className="text-orange-600 h-3.5 w-3.5"
                />
                <span className="text-sm font-medium text-gray-700">
                  Purchases
                </span>
              </div>
              <span className="text-sm font-bold text-orange-700">
                {formatCurrency(totalPurchases)}
              </span>
            </div>
            <div className="flex items-center justify-between p-2 rounded-lg bg-red-50 border border-red-200">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faReceipt}
                  className="text-red-600 h-3.5 w-3.5"
                />
                <span className="text-sm font-medium text-gray-700">
                  Other expenses
                </span>
              </div>
              <span className="text-sm font-bold text-red-700">
                {formatCurrency(totalExpenses)}
              </span>
            </div>
            <div
              className={`flex items-center justify-between p-2 rounded-lg border-2 ${
                profitLoss >= 0
                  ? "bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-400"
                  : "bg-gradient-to-r from-rose-50 to-red-50 border-rose-400"
              }`}
            >
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={profitLoss >= 0 ? faArrowUp : faArrowDown}
                  className={
                    profitLoss >= 0
                      ? "text-emerald-600 h-3.5 w-3.5"
                      : "text-rose-600 h-3.5 w-3.5"
                  }
                />
                <span className="text-sm font-semibold text-gray-700">
                  {profitLoss >= 0 ? "Profit" : "Loss"}
                </span>
              </div>
              <span
                className={`text-sm font-bold ${profitLoss >= 0 ? "text-emerald-700" : "text-rose-700"}`}
              >
                {formatCurrency(Math.abs(profitLoss))}
              </span>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faMoneyBillWave}
                  className="text-purple-700 h-3.5 w-3.5"
                />
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">
                  Total Receive
                </p>
                <p className="text-sm font-bold text-purple-700">
                  {formatCurrency(totalReceive)}
                </p>
              </div>
            </div>
            <Badge
              variant="outline"
              className="bg-purple-200 text-purple-700 border-purple-300"
            >
              {receiveRows.length} entries
            </Badge>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200 p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-indigo-200 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faChartPie}
                  className="text-indigo-700 h-3.5 w-3.5"
                />
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">
                  Total Transactions
                </p>
                <p className="text-sm font-bold text-indigo-700">
                  {filteredTransactions.length}
                </p>
              </div>
            </div>
            <Badge
              variant="outline"
              className="bg-indigo-200 text-indigo-700 border-indigo-300"
            >
              All records
            </Badge>
          </div>
        </Card>

        <Card
          className={`border-2 p-3 ${
            netBalance >= 0
              ? "bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-300"
              : "bg-gradient-to-br from-rose-50 to-red-50 border-rose-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  netBalance >= 0 ? "bg-emerald-200" : "bg-rose-200"
                }`}
              >
                <FontAwesomeIcon
                  icon={netBalance >= 0 ? faArrowUp : faArrowDown}
                  className={
                    netBalance >= 0
                      ? "text-emerald-700 h-3.5 w-3.5"
                      : "text-rose-700 h-3.5 w-3.5"
                  }
                />
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">
                  Current Status
                </p>
                <p
                  className={`text-sm font-bold ${netBalance >= 0 ? "text-emerald-700" : "text-rose-700"}`}
                >
                  {netBalance >= 0 ? "Profitable" : "At Loss"}
                </p>
              </div>
            </div>
            <Badge
              variant="outline"
              className={
                netBalance >= 0
                  ? "bg-emerald-200 text-emerald-700 border-emerald-300"
                  : "bg-rose-200 text-rose-700 border-rose-300"
              }
            >
              {netBalance >= 0 ? "Good" : "Needs Review"}
            </Badge>
          </div>
        </Card>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab("shopinfo")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
            activeTab === "shopinfo"
              ? "border-blue-800 text-blue-800"
              : "border-transparent text-gray-600 hover:text-gray-700 hover:border-gray-300"
          }`}
        >
          <FontAwesomeIcon icon={faInfoCircle} className="h-4 w-4" />
          Shop information
        </button>
        {canViewFinancialOverview && (
          <button
            onClick={() => setActiveTab("financial")}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
              activeTab === "financial"
                ? "border-blue-800 text-blue-800"
                : "border-transparent text-gray-600 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <FontAwesomeIcon icon={faChartBar} className="h-4 w-4" />
            Financial overview
          </button>
        )}
      </div>

      {activeTab === "shopinfo" || !canViewFinancialOverview ? (
        <ShopInfoContent />
      ) : (
        <FinancialOverviewContent />
      )}
    </div>
  );
};

export default ShopInfoTab;
